import { cleanNucleotides } from "@/lib/tool-kit/biology-genetics/iupac";
import { MATRIX_ORDER } from "@/lib/tool-kit/biology-genetics/substitution-matrices";

export type SequenceKind = "dna" | "protein";

const LETTERS = /[A-Za-z]/g;
const NUCLEOTIDE_ONLY = /[ACGTUN]/g;
const SPACE_AND_DIGITS = /[\s\d]+/g;
/** Letters the protein matrices do not list stand for "unknown". */
const UNLISTED = /[JOU]/g;

/** DNA when at least 90% of the letters are A, C, G, T, U or N; otherwise protein. */
export function detectKind(...texts: string[]): SequenceKind {
  const joined = texts.join("");
  const letters = joined.match(LETTERS)?.length ?? 0;
  if (letters === 0) {
    return "dna";
  }
  const nucleotide = joined.toUpperCase().match(NUCLEOTIDE_ONLY)?.length ?? 0;
  return nucleotide / letters >= 0.9 ? "dna" : "protein";
}

export interface CleanedSequence {
  sequence: string;
  /** Distinct characters that were dropped. */
  dropped: string[];
}

/** Reduce pasted text to residues: DNA keeps IUPAC codes (U read as T); protein keeps A–Z. */
export function cleanResidues(
  raw: string,
  kind: SequenceKind
): CleanedSequence {
  if (kind === "dna") {
    const cleaned = cleanNucleotides(raw);
    return {
      sequence: cleaned.sequence.replaceAll("U", "T"),
      dropped: cleaned.invalid,
    };
  }
  const stripped = raw.replace(SPACE_AND_DIGITS, "").toUpperCase();
  const dropped = new Set<string>();
  let sequence = "";
  for (const character of stripped) {
    if (character >= "A" && character <= "Z") {
      sequence += character;
    } else if (character !== "*") {
      dropped.add(character);
    }
  }
  return { sequence: sequence.replace(UNLISTED, "X"), dropped: [...dropped] };
}

/** Is every letter one the substitution matrices score? (B, Z and X are listed.) */
export function isScoredProtein(sequence: string): boolean {
  return [...sequence].every((residue) => MATRIX_ORDER.includes(residue));
}
